import { checkAuthStatus } from "@/src/utils/auth"
import {
  saveAuthToken,
  type PackageManager,
} from "@/src/utils/package-manager-config"
import { logger } from "@/src/utils/logger"
import { colors } from "@/src/utils/colors"
import { spinner } from "@/src/utils/spinner"
import { getPackageManager } from "./get-package-manager"
import { PAID_PLANS, PaidPlan, Plan } from "./registry/schema"
import { createThemedConfirm, handleLogin } from "../commands/auth"
import { tokenStorage } from "@/src/utils/bootstrap"

/**
 * Checks if the selected components contain any paid components
 */
export function hasPaidComponents(
  components: Array<{ name: string; plans: Plan[] }>
): boolean {
  return components.some(
    (component) =>
      component.plans &&
      component.plans.some((plan) => PAID_PLANS.includes(plan as PaidPlan))
  )
}

/**
 * Prompts user to accept Pro license and Terms of Service for paid components
 */
async function promptLicenseAcceptance(
  selectedComponents: Array<{ name: string; plans: Plan[] }>
): Promise<boolean> {
  const paidComponents = selectedComponents.filter(
    (component) =>
      component.plans &&
      component.plans.some((plan) => PAID_PLANS.includes(plan as PaidPlan))
  )

  if (paidComponents.length === 0) {
    return true
  }

  // Check if license was already accepted
  if (tokenStorage.hasAcceptedLicense()) {
    logger.log(`${colors.cyan("✔")} Pro license accepted`)
    return true
  }

  logger.log("")
  logger.log(colors.yellow("📋 License Agreement"))
  logger.log("")
  logger.log("Integrating paid components requires accepting:")
  logger.log(
    `  • Pro License: ${colors.cyan("https://tiptap.dev/pro-license")}`
  )
  logger.log(
    `  • Terms of Service: ${colors.cyan("https://tiptap.dev/terms-of-service")}`
  )
  logger.log("")
  logger.log(colors.gray("  Valid during trial and active subscription"))
  logger.log("")

  try {
    const accepted = await createThemedConfirm(
      "Do you accept the Pro license and terms of service?",
      false
    )

    if (!accepted) {
      logger.log(colors.gray("License not accepted"))
      logger.log(colors.gray("Tip: You can still install free components"))
      return false
    }

    // Store license acceptance
    tokenStorage.setLicenseAccepted()
    logger.log(`${colors.cyan("✔")} License accepted`)
    return true
  } catch (error) {
    logger.log(colors.gray("License acceptance cancelled"))
    return false
  }
}

/**
 * Ensures user is authenticated for paid components and has proper .npmrc configuration.
 */
export async function ensureAuthForPaidComponents(
  selectedComponents: Array<{ name: string; plans: Plan[] }>,
  cwd: string
): Promise<boolean> {
  try {
    if (!hasPaidComponents(selectedComponents)) {
      return true
    }

    const authStatus = await checkAuthStatus()

    if (!authStatus.authenticated) {
      logger.log("")
      logger.log(colors.yellow("🔐 Authentication Required"))
      logger.log("")
      logger.log("Pro components require authentication.")
      logger.log(
        `Run ${colors.cyan("tiptap login")} or create an account at ${colors.cyan("https://cloud.tiptap.dev")}`
      )
      logger.log("")

      const shouldLoginNow = await createThemedConfirm(
        "Would you like to login now?",
        true
      )

      if (!shouldLoginNow) {
        logger.log("")
        logger.log(colors.gray("Authentication cancelled"))
        logger.log(colors.gray("Tip: You can still install free components"))
        return false
      }

      const loginSuccess = await handleLogin({
        cwd,
        writeConfig: true,
        showMessage: false,
      })
      if (!loginSuccess) {
        return false
      }
    } else {
      // Always (re)write the project config from the in-memory token. The
      // registry rotates the npm `authToken` on each login, invalidating older
      // ones, so any token already written to a global `~/.npmrc` or to the
      // project may be stale. Trusting it leads to silent 403s during install.
      const packageManager = await getPackageManager(cwd)
      await createNpmrcConfiguration(packageManager, cwd)
    }

    // After authentication is complete, prompt for license acceptance
    const licenseAccepted = await promptLicenseAcceptance(selectedComponents)
    if (!licenseAccepted) {
      return false
    }

    return true
  } catch (error) {
    logger.error(
      `Authentication check failed: ${error instanceof Error ? error.message : "Unknown error"}`
    )
    return false
  }
}

/**
 * Creates .npmrc configuration for the project
 */
async function createNpmrcConfiguration(
  packageManager: PackageManager,
  cwd: string
) {
  const configSpinner = spinner("Creating registry configuration...")?.start()

  try {
    const authStatus = await checkAuthStatus()

    if (!authStatus.authenticated) {
      configSpinner?.fail(
        "Cannot create configuration - user not authenticated"
      )
      return false
    }

    const authToken = tokenStorage.getAuthToken()

    if (!authToken) {
      configSpinner?.fail("No authentication token found")
      return false
    }

    const success = await saveAuthToken(authToken, packageManager, cwd)
    if (!success) {
      configSpinner?.fail("Failed to save authentication token")
      return false
    }

    configSpinner?.stopAndPersist({
      symbol: colors.cyan("✔"),
      text: ".npmrc created successfully",
    })
  } catch (error) {
    configSpinner?.fail("Failed to create registry configuration")
    logger.error(
      `Configuration error: ${error instanceof Error ? error.message : "Unknown error"}`
    )
  }
}
